// /app/content — Content Studio landing.
//
// v0.1.0: pick a brand (or jump straight to "+ New" if you only have one).
// Once selected, the user is sent to /app/content/$brandId where we show
// recent generations + posts for that brand.
//
// Future v0.1.1: cross-brand "all recent" view + scheduled-post calendar.
import { useEffect, useState } from "react";
import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { useI18n } from "../../../lib/i18n";
import { brands as brandsAPI, type Brand } from "../../../lib/api";

function ContentHome() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [state, setState] = useState<
    { kind: "loading" } | { kind: "ready"; list: Brand[] } | { kind: "error"; msg: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    brandsAPI.list()
      .then((r) => !cancelled && setState({ kind: "ready", list: r.brands }))
      .catch((e) => !cancelled && setState({ kind: "error", msg: String(e) }));
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <Card><p className="text-secondary">{t("common.loading")}</p></Card>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <Card><p className="text-error">{state.msg}</p></Card>
      </div>
    );
  }
  // Exactly one brand → bounce straight to its content page. Users with
  // one brand shouldn't have to keep picking it.
  if (state.list.length === 1) {
    return <Navigate to="/app/content/$brandId" params={{ brandId: state.list[0]!.id }} replace />;
  }
  // Zero brands → tell them to create one. Content makes no sense without
  // a brand.
  if (state.list.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <Card>
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <span className="eyebrow eyebrow-pill inline-flex">{t("content.pickBrand")}</span>
            <p className="text-secondary text-sm">{t("brands.empty.subtitle")}</p>
            <Button variant="primary" onClick={() => nav({ to: "/app/brands/new" })}>
              + {t("brands.new")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  // Multiple brands → grid picker.
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <span className="eyebrow">{t("content.eyebrow")}</span>
        <h1 className="font-display text-3xl font-semibold">{t("content.title")}</h1>
        <p className="text-secondary text-sm">{t("content.pickBrand")}</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {state.list.map((b) => (
          <Card key={b.id} className="!p-5 hover:bg-overlay/40 transition h-full flex flex-col gap-2">
            <Link
              to="/app/content/$brandId"
              params={{ brandId: b.id }}
              className="ring-nexus rounded -m-2 p-2 flex flex-col gap-2"
            >
              <span className="eyebrow">{b.country} · {b.primary_language}</span>
              <h2 className="font-display text-lg font-semibold truncate" title={b.trade_name}>{b.trade_name}</h2>
              <p className="text-xs text-muted font-mono truncate" title={b.slug}>/{b.slug}</p>
            </Link>
            {/* Quick-access calendar link per brand — operator feedback
                2026-05-20: "calendar is in brand not in content menu".
                Surfacing it here too gives a one-click jump from the
                Content area into the publishing timeline. */}
            <div className="flex justify-end mt-1">
              <Link
                to="/app/brands/$brandId/calendar"
                params={{ brandId: b.id }}
                className="text-xs text-secondary hover:text-primary underline ring-nexus rounded"
              >
                📅 {t("content.openCalendar")}
              </Link>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/app/content/")({
  component: ContentHome,
});
